/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import {
  DialogWrapper,
  PopupHeadingText,
  DeleteWithdrawText,
  CloseIconStyle,
} from '../../../Vendor/Components/CreateBid.Style';
import Button, { ButtonType } from '../../../../Shared/Components/Common/Buttons/Button';
import { Icons } from '../../../../Shared/Constants/Icons';
import { useForm } from 'react-hook-form';
import DateSelection from '../../../../Shared/Components/Common/DatePicker/DateSelection';
import theme from '../../../../Shared/Themes/theme';
import { JobClosureFormFields } from '../../Models/ConfirmPopUp.model';
import { ClosureDateContainer } from '../../../Vendor/Components/AllSchedules/AllJobs.Style';

interface ShiftSummary {
  scheduled: number;
  completed: number;
  inProgress: number;
  noShow: number;
  total: number;
}

interface ConfirmationDialogProps {
  open: boolean;
  handleClose: () => void;
  handleOnCloseJob: (details: { ClosureDate: string }) => void;
  isCompleteJobLoading: boolean;
  jobStartDate: string;
  showDatePicker?: boolean;
  shiftSummary?: ShiftSummary | null;
}

const ConfirmPopup: React.FC<ConfirmationDialogProps> = ({
  open,
  handleClose,
  handleOnCloseJob,
  isCompleteJobLoading,
  jobStartDate,
  showDatePicker,
  shiftSummary,
}) => {
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      [JobClosureFormFields.ClosureDate]: new Date().toISOString().split('T')[0],
    },
  });
  useEffect(() => {
    setValue(JobClosureFormFields.ClosureDate, new Date().toISOString().split('T')[0]);
  }, []);
  const { t } = useTranslation(['assignJob']);
  return (
    <DialogWrapper open={open} onClose={handleClose}>
      <CloseIconStyle onClick={handleClose}>
        <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
      </CloseIconStyle>
      <PopupHeadingText>{t('assignJob:markTheJobAsComplete')}</PopupHeadingText>
      {shiftSummary ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: theme.palette.secondary.customColor17,
            borderRadius: '0.5rem',
          }}>
          {[
            { label: t('assignJob:noOfScheduledShifts'), value: shiftSummary.scheduled },
            { label: t('assignJob:noOfCompletedShifts'), value: shiftSummary.completed },
            { label: t('assignJob:noOfInProgressShifts'), value: shiftSummary.inProgress },
            { label: t('assignJob:noOfNoShowShifts'), value: shiftSummary.noShow },
            { label: t('assignJob:totalShifts'), value: shiftSummary.total },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ minWidth: '170px' }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mr: '0.25rem' }}>
                :
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '2rem' }}>
        <DeleteWithdrawText>{t('assignJob:confirmCompleteTheJob')}</DeleteWithdrawText>
      </Box>
      {showDatePicker && (
        <ClosureDateContainer>
          <DateSelection
            withPortal={true}
            requiredText={t('calendar:dateFieldIsRequired')}
            control={control}
            fieldName={JobClosureFormFields.ClosureDate}
            label={t('homePage:ClosureDate')}
            isExcludePastDate={false}
            isExcludeFutureDate={false}
            showSpecificRangeDates={{ from: new Date(jobStartDate), to: undefined }}
            textFieldStyles={{
              '& .MuiOutlinedInput-root': {
                '& fieldset,&:hover fieldset,&.Mui-focused fieldset': {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.dark,
                  borderRadius: '1rem',
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.primary.dark,
                '&.Mui-focused': {
                  color: theme.palette.primary.dark,
                },
              },
            }}
          />
        </ClosureDateContainer>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1.5rem',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
        }}>
        <Button type={ButtonType.Secondary} label={t('assignJob:no')} onClick={handleClose} />
        <Button
          type={ButtonType.Primary}
          label={t('assignJob:yes')}
          onClick={handleSubmit(handleOnCloseJob)}
          disabled={isCompleteJobLoading}
          isLoading={isCompleteJobLoading}
        />
      </Box>
    </DialogWrapper>
  );
};

export default ConfirmPopup;
